// Advertiser-facing sign-in modal (CTV-591).
//
// Rendered from public advertiser surfaces: PublicHeader, /my-campaigns,
// campaign detail, etc. Portal-rendered so it escapes the header's stacking
// context. Per-station brand accent on the primary CTA (via PrimaryButton's
// accentColor / primaryColor) — that per-station brand is the reason this
// modal isn't unified with StaffSignInForm (see ADR 0021).
//
// Stages (see CONTEXT.md → "Advertiser sign-in surface"):
//   • email-password       — default. Email + password + Sign in. Secondary
//                            links: "Sign in with a code instead" and
//                            "Forgot password?".
//   • otp-email            — email-only + Send code. Reachable from the
//                            secondary link above and from the "try email
//                            code instead" hint after a failed password sign-in.
//   • otp-code             — 6-digit code + Verify. After verify succeeds,
//                            checks `hasPassword`:
//                              hasPassword=false → 'set-password-optional'
//                              hasPassword=true  → close modal
//   • set-password-optional — password + confirm + Set password (or Skip).
//                            Piggy-backs on the OTP trust anchor (ADR 0021
//                            decision 2) — no extra email round-trip.
//   • forgot-sent          — banner "check your email." Reached from the
//                            Forgot password? link. The dispatched template
//                            branches on the target user's `encrypted_password`
//                            server-side: password-havers get
//                            `auth.password_reset`, non-password-havers get
//                            `auth.password_setup`.
import React from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { X, Mail, KeyRound, Lock, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useAuth } from './useAuth';
import { usePublisherConfig } from '../../../data/publisherConfigs';
import { selfServiceSupabase } from '../../../lib/selfServiceSupabase';
import { apiClient } from '../../../lib/apiClient';
import PrimaryButton from '../shared/PrimaryButton';
import Banner from '../shared/Banner';

interface SignInModalProps {
  open: boolean;
  onClose: () => void;
  initialEmail?: string;
}

type Stage =
  | 'email-password'
  | 'otp-email'
  | 'otp-code'
  | 'set-password-optional'
  | 'forgot-sent';

const SignInModal: React.FC<SignInModalProps> = ({ open, onClose, initialEmail = '' }) => {
  const { requestOtp, verifyOtp, refreshHasPassword } = useAuth();
  const { slug } = useParams<{ slug: string }>();
  const { config } = usePublisherConfig(slug);
  const accent = config?.accentColor || config?.primaryColor;
  const primary = config?.primaryColor;

  const [stage, setStage] = React.useState<Stage>('email-password');
  const [email, setEmail] = React.useState(initialEmail);
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [code, setCode] = React.useState('');
  const [devCode, setDevCode] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [showOtpHint, setShowOtpHint] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setStage('email-password');
      setEmail(initialEmail);
      setPassword('');
      setConfirmPassword('');
      setCode('');
      setDevCode(null);
      setError(null);
      setShowOtpHint(false);
    }
  }, [open, initialEmail]);

  if (!open) return null;

  const handlePasswordSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selfServiceSupabase) {
      setError('Supabase Auth is not configured.');
      return;
    }
    if (!email.includes('@')) {
      setError('Enter a valid email');
      return;
    }
    if (password.length < 1) {
      setError('Enter your password');
      return;
    }
    setError(null);
    setShowOtpHint(false);
    setBusy(true);
    try {
      const { error: authErr } = await selfServiceSupabase.auth.signInWithPassword({
        email,
        password,
      });
      if (authErr) throw authErr;
      // Session change flows through useAuth's onAuthStateChange listener.
      // The user already has a password (they just used it), so no post-verify
      // nudge — close the modal.
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid email or password');
      setShowOtpHint(true);
    } finally {
      setBusy(false);
    }
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.includes('@')) {
      setError('Enter a valid email');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const { devCode: dev } = await requestOtp(email, slug);
      setDevCode(dev);
      setCode('');
      setStage('otp-code');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send code');
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError('Enter the 6-digit code');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await verifyOtp(email, code);
      // OTP verified. Ask the context for the fresh has-password value — its
      // return type is `boolean | null`, and `null` means the lookup failed
      // (network / auth race). Default to *showing* the nudge in the null
      // case: users who signed in via OTP came here most likely because
      // they don't have a password yet, and skipping the modal silently
      // would strand a first-time user with no obvious way to set one.
      // Password-havers hit "Skip for now"; that's cheaper than missing the
      // nudge for someone who legitimately needs it.
      const hasPasswordNow = await refreshHasPassword();
      if (hasPasswordNow === true) {
        onClose();
      } else {
        setPassword('');
        setConfirmPassword('');
        setStage('set-password-optional');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selfServiceSupabase) {
      setError('Supabase Auth is not configured.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      // Combine the password update with a `password_set: true` metadata
      // write. The backend `hasPassword` signal keys off metadata (not
      // `encrypted_password`, which Supabase populates on every user-
      // creation path — see hasUserSetPassword in email-dispatch.controller).
      const { error: updErr } = await selfServiceSupabase.auth.updateUser({
        password,
        data: { password_set: true },
      });
      if (updErr) throw updErr;
      void refreshHasPassword();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set password');
    } finally {
      setBusy(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email.includes('@')) {
      setError('Enter your email first, then click "Forgot password?".');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await apiClient.post(
        '/auth/password-reset/request',
        {
          email,
          stationSlug: slug,
          redirectTo: `${window.location.origin}/reset-password`,
        },
        { anonymous: true },
      );
      setStage('forgot-sent');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email');
    } finally {
      setBusy(false);
    }
  };

  const headerTitle: Record<Stage, string> = {
    'email-password': 'Sign in',
    'otp-email': 'Sign in with a code',
    'otp-code': 'Enter your code',
    'set-password-optional': 'Set a password',
    'forgot-sent': 'Check your email',
  };

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-md bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">{headerTitle[stage]}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 -mr-1 text-slate-500 hover:text-slate-900"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {stage === 'email-password' && (
          <form onSubmit={handlePasswordSignIn} className="p-5 space-y-4">
            <label className="block">
              <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">
                Email
              </span>
              <div className="mt-1 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@business.com"
                  className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">
                Password
              </span>
              <div className="mt-1 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
            </label>
            {error && (
              <div className="flex items-start gap-2 text-xs text-red-600">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <div>
                  {error}
                  {showOtpHint && (
                    <>
                      {' '}—{' '}
                      <button
                        type="button"
                        onClick={() => {
                          setError(null);
                          setShowOtpHint(false);
                          setStage('otp-email');
                        }}
                        className="underline font-medium hover:text-red-700"
                      >
                        try email code instead
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
            <PrimaryButton
              type="submit"
              disabled={busy}
              accentColor={accent}
              primaryColor={primary}
              fullWidth
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              Sign in
            </PrimaryButton>
            <div className="flex items-center justify-between text-xs text-slate-600 pt-1">
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={busy}
                className="hover:text-slate-900 hover:underline disabled:opacity-50"
              >
                Forgot password?
              </button>
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setShowOtpHint(false);
                  setStage('otp-email');
                }}
                disabled={busy}
                className="hover:text-slate-900 hover:underline disabled:opacity-50 text-right"
              >
                Don't have a password?
                <br />
                Sign in with a code
              </button>
            </div>
          </form>
        )}

        {stage === 'otp-email' && (
          <form onSubmit={handleSendOtp} className="p-5 space-y-4">
            <p className="text-sm text-slate-600">
              We'll send a 6-digit code to your email. No password needed.
            </p>
            <label className="block">
              <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">
                Email
              </span>
              <div className="mt-1 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@business.com"
                  className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
            </label>
            {error && <p className="text-xs text-red-600">{error}</p>}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setStage('email-password');
                }}
                className="px-3.5 py-1.5 rounded-full text-[12px] font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              >
                Back
              </button>
              <PrimaryButton
                type="submit"
                disabled={busy}
                accentColor={accent}
                primaryColor={primary}
                fullWidth
                className="flex-1"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Send code
              </PrimaryButton>
            </div>
          </form>
        )}

        {stage === 'otp-code' && (
          <form onSubmit={handleVerifyOtp} className="p-5 space-y-4">
            <p className="text-sm text-slate-600">
              We sent a code to <span className="font-medium text-slate-900">{email}</span>.
            </p>
            {devCode && (
              <Banner tone="caution" label="Demo mode">
                Your code is{' '}
                <span className="font-mono font-bold text-[13px] text-amber-950">{devCode}</span>.
                Paste it below.
              </Banner>
            )}
            <label className="block">
              <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">
                6-digit code
              </span>
              <div className="mt-1 relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm font-mono tracking-widest focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
            </label>
            {error && <p className="text-xs text-red-600">{error}</p>}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setCode('');
                  setStage('otp-email');
                }}
                className="px-3.5 py-1.5 rounded-full text-[12px] font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              >
                Back
              </button>
              <PrimaryButton
                type="submit"
                disabled={busy}
                accentColor={accent}
                primaryColor={primary}
                fullWidth
                className="flex-1"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Verify &amp; sign in
              </PrimaryButton>
            </div>
          </form>
        )}

        {stage === 'set-password-optional' && (
          <form onSubmit={handleSetPassword} className="p-5 space-y-4">
            <p className="text-sm text-slate-600">
              You're signed in. Set a password now so you can skip the email code
              next time — or skip for now and stay code-only.
            </p>
            <label className="block">
              <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">
                New password
              </span>
              <div className="mt-1 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="password"
                  autoComplete="new-password"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">
                Confirm password
              </span>
              <div className="mt-1 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-type your new password"
                  className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
            </label>
            {error && <p className="text-xs text-red-600">{error}</p>}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="px-3.5 py-1.5 rounded-full text-[12px] font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors disabled:opacity-50"
              >
                Skip for now
              </button>
              <PrimaryButton
                type="submit"
                disabled={busy}
                accentColor={accent}
                primaryColor={primary}
                fullWidth
                className="flex-1"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Set password
              </PrimaryButton>
            </div>
          </form>
        )}

        {stage === 'forgot-sent' && (
          <div className="p-5 space-y-4">
            <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-md p-4">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
              <div className="text-sm">
                <div className="font-semibold">Check your email</div>
                <p className="mt-1">
                  We sent a link to <span className="font-medium">{email}</span>. Click
                  it to set your password.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setError(null);
                setStage('email-password');
              }}
              className="w-full py-2 text-sm text-slate-600 hover:text-slate-900"
            >
              Back to sign in
            </button>
          </div>
        )}
      </div>
    </>,
    document.body,
  );
};

export default SignInModal;
